import React, { useState } from 'react';
import { locName, subLocationNameFree } from '../../utils/locations';

// Add / edit a manual sub-location.
//
// Replaces the window.prompt the Add button first shipped with. Same reasoning as
// LocationForm: only the name is required, so a sub-location can be created from what
// someone knows now and filled in later.
//
// A portal sub-location never reaches this form — its name and parent are owned by the
// portal, and editing them here would be undone on the next sync.

const SECTION_LABEL = {
  fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em',
  textTransform: 'uppercase', color: 'var(--text-muted)',
};

function Field({ label, value, onChange, placeholder, required, hint, invalid, autoFocus }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <label style={{ display: 'flex', alignItems: 'baseline', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
        {label}
        {required && <span style={{ color: 'var(--brand-light)' }}>*</span>}
      </label>
      <input
        type="text"
        value={value}
        autoFocus={autoFocus}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        style={{
          height: 34, padding: '0 10px', borderRadius: 6, outline: 'none',
          border: `1px solid ${invalid ? 'var(--danger-border)' : 'var(--border-default)'}`,
          background: 'var(--surface-default)', color: 'var(--text-primary)',
          fontFamily: 'inherit', fontSize: 14,
        }} />
      {hint && <span style={{ fontSize: 12, color: invalid ? 'var(--danger-text)' : 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

function SubLocationForm({ sub, loc, st, onSave, onCancel, onArchive }) {
  const editing = !!sub;
  const site = locName(loc);

  const [f, setF] = useState(() => ({
    name: (editing && sub.name) || '',
    contact: (editing && sub.contact) || '',
    contactRole: (editing && sub.contactRole) || '',
    phone: (editing && sub.phone) || '',
    email: (editing && sub.email) || '',
    note: (editing && sub.note) || '',
  }));
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const name = f.name.trim();
  // Name is the join key — device.subLocation stores it — so a duplicate would silently
  // merge two places into one. Checked against both mechanisms; editing ignores itself.
  const taken = !!name && !subLocationNameFree(name, site, st, editing ? sub.id : null);
  const canSave = !!name && !taken;

  const held = editing
    ? (st.devices || []).filter(d => d.location === site && d.subLocation === sub.name).length
    : 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={editing ? `Edit ${sub.name}` : `Add a sub-location to ${site}`}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="card" style={{ width: 520, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border-default)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
              {editing ? 'Edit sub-location' : 'Add a sub-location'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Within {site}</div>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
        </div>

        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field
            label="Name" required autoFocus
            value={f.name} onChange={v => set('name', v)}
            placeholder="Crewe Depot"
            invalid={taken}
            hint={taken
              ? `${site} already has a sub-location called “${name}”.`
              : 'What this place is called on site. Devices are tagged with it.'} />

          {editing && held > 0 && name !== sub.name && (
            <div style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--warning-border)', background: 'var(--surface-inset)', fontSize: 12, color: 'var(--warning-text)' }}>
              {held} device{held === 1 ? '' : 's'} {held === 1 ? 'is' : 'are'} tagged “{sub.name}”. Renaming
              re-tags {held === 1 ? 'it' : 'them'} to “{name}” so nothing is stranded.
            </div>
          )}

          <div>
            <div style={{ ...SECTION_LABEL, marginBottom: 10 }}>Contact</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Contact name" value={f.contact} onChange={v => set('contact', v)} placeholder="Dan Whitcombe" hint="Optional" />
              <Field label="Role" value={f.contactRole} onChange={v => set('contactRole', v)} placeholder="Site supervisor" hint="Optional" />
              <Field label="Phone" value={f.phone} onChange={v => set('phone', v)} placeholder="07700 900123" hint="Optional" />
              <Field label="Email" value={f.email} onChange={v => set('email', v)} placeholder="name@client.co.uk" hint="Optional" />
            </div>
          </div>

          <Field
            label="Note" value={f.note} onChange={v => set('note', v)}
            placeholder="Access via the north gate" hint="Optional — anything someone arriving should know" />

          {editing && onArchive && (
            <div style={{ paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
              <button
                type="button"
                onClick={onArchive}
                style={{ height: 32, padding: '0 12px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--surface-default)', color: 'var(--danger-text)', fontFamily: 'inherit', fontSize: 13, cursor: 'pointer' }}>
                Archive this sub-location
              </button>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                It stops being a choice when deploying. {held
                  ? `The ${held} device${held === 1 ? '' : 's'} tagged with it stay on ${site}.`
                  : 'Nothing is tagged with it.'}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, padding: '14px 18px', borderTop: '1px solid var(--border-default)' }}>
          <button type="button" onClick={onCancel} className="btn btn-ghost">Cancel</button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => onSave({ ...f, name })}
            className="btn btn-primary"
            style={{ opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }}>
            {editing ? 'Save changes' : 'Add sub-location'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SubLocationForm;
